import React, { useState } from 'react';
import { ageNote } from '../../utils/transferReturns';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { findArcaPlanByName, ArcaPlan } from '../../constants/arcaPlans';
import { INDIVIDUAL_PLANS_CONTENT } from '../../data/individualPlansContent';
import toTitleCase from '../../utils/toTitleCase';
import { fmtNum } from '../../utils/formatArcaNumbers';
import useArcaPlanReturns from '../../hooks/useArcaPlanReturns';
import { periodLabel } from './projectionLegend';
import { PensionPlanTransfer } from '../../api/pension-plan-transfers/interfaces/PensionPlanTransfer';
import updateMyTransfer from '../../api/pension-plan-transfers/updateMyTransfer';

interface OtherPlansSheetProps {
	open: boolean;
	transfer: PensionPlanTransfer;
	token: string;
	plans: ArcaPlan[];
	translate: (key: string) => string;
	onClose: () => void;
	onPlanChanged: (updatedTransfer: PensionPlanTransfer) => void;
	/**
	 * Las otras peticiones vivas del funnel (plan individual en proceso: el alta y el
	 * traspaso). El plan elegido se escribe también en ellas, o la ejecución del alta
	 * no encontraría su petición por plan.
	 */
	siblingTransferIds?: string[];
	/** Años hasta la jubilación, si se saben: los planes de ciclo de vida, con su tramo de hoy. */
	yearsToRetirement?: number;
}

const OtherPlansSheet: React.FC<OtherPlansSheetProps> = ({
	open,
	transfer,
	token,
	plans,
	translate,
	onClose,
	onPlanChanged,
	siblingTransferIds = [],
	yearsToRetirement
}) => {
	const currentPlanId =
		transfer.selectedArcaPlanId ||
		transfer.metadata?.selectedPlanId ||
		findArcaPlanByName(transfer.metadata?.plan_name ?? '', plans)?.id;
	const [selected, setSelected] = useState<ArcaPlan | null>(null);
	// Rentabilidad de cada plan, de comparsa: la pide la propia lista (una vez por sesión), así
	// funciona desde cualquier pantalla que la abra. `undefined` = cargando.
	const planReturns = useArcaPlanReturns(token, yearsToRetirement);
	const [saving, setSaving] = useState(false);

	const handleConfirm = async () => {
		if (!selected || selected.id === currentPlanId) {
			onClose();
			return;
		}
		setSaving(true);
		try {
			const res = await updateMyTransfer(token, transfer.id, {
				selectedArcaPlanId: selected.id
			});
			if (res.success && res.data) {
				// La principal ya está guardada: la pantalla tiene que enterarse aunque luego
				// falle una hermana, o seguiría enseñando el plan viejo.
				onPlanChanged(res.data.transfer);
				await Promise.all(
					siblingTransferIds.map((siblingId) =>
						updateMyTransfer(token, siblingId, { selectedArcaPlanId: selected.id }).catch(
							(siblingError) =>
								console.error('Error updating plan selection on sibling request:', siblingError)
						)
					)
				);
			}
		} catch (err) {
			console.error('Error updating plan selection:', err);
		} finally {
			setSaving(false);
			onClose();
		}
	};

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="sm:max-w-lg w-full max-h-[90dvh] overflow-y-auto p-0 gap-0 sm:rounded-2xl rounded-t-2xl fixed bottom-0 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 translate-y-0">
				<DialogHeader className="p-6 pb-4">
					<DialogTitle className="text-lg font-semibold">
						{translate('individual.otherPlans.title') || 'Estas son tus opciones'}
					</DialogTitle>
					<DialogDescription className="text-sm text-muted-foreground">
						{translate('individual.otherPlans.subtitle') ||
							'Las opciones son válidas. Cada una tiene su tesis. Tú eliges la que más te resuene.'}
					</DialogDescription>
				</DialogHeader>

				<div className="px-6 pb-4 space-y-3">
					{plans.map((plan) => {
						const content = INDIVIDUAL_PLANS_CONTENT[plan.catalogSlug];
						const isCurrentPlan = plan.id === currentPlanId;
						const isSelected = selected ? selected.id === plan.id : isCurrentPlan;

						return (
							<button
								key={plan.id}
								onClick={() => setSelected(plan)}
								className={cn(
									'w-full text-left p-4 rounded-xl border-2 transition-all duration-200',
									isSelected
										? 'border-primary bg-primary/5'
										: 'border-border hover:border-primary/40 hover:bg-muted/30'
								)}
							>
								<div className="flex items-center gap-3 mb-2">
									<img
										src={plan.logo}
										alt={plan.manager}
										className="w-9 h-9 rounded-lg object-contain bg-white border border-border p-0.5 shrink-0"
									/>
									<div>
										{content && (
											<span className="text-xs font-medium text-primary">
												{content.philosophyTagline}
											</span>
										)}
										<p className="text-sm font-semibold leading-tight">{toTitleCase(plan.name)}</p>
									</div>
									{isCurrentPlan && (
										<span className="ml-auto text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full shrink-0">
											Tu elección
										</span>
									)}
								</div>
								{content && (
									<p className="text-xs text-muted-foreground leading-relaxed">
										{content.philosophy}
									</p>
								)}
								{(() => {
									const r = planReturns?.[plan.catalogSlug];
									return r ? (
										<div className="flex gap-4 mt-2 text-xs text-muted-foreground">
											<span>
												<strong className="text-foreground">{fmtNum(r.annualPct)}%</strong>{' '}
												{translate('individual.otherPlans.netReturnShort') || 'rent. neta'}{' '}
												{periodLabel(translate, r.periodYears)}
												{ageNote(translate, r)}
											</span>
											{r.managementFeePct !== null && (
												<span>
													<strong className="text-foreground">{fmtNum(r.managementFeePct)}%</strong>{' '}
													{translate('individual.otherPlans.feeMgmt') || 'comisión de gestión'}
												</span>
											)}
										</div>
									) : (
										<p className="mt-2 text-xs text-muted-foreground">
											{planReturns === undefined
												? translate('individual.exploration.chosenPlan.loadingReturns') ||
													'Cargando rentabilidad…'
												: translate('individual.exploration.chosenPlan.noReturns') ||
													'Sin datos de rentabilidad'}
										</p>
									);
								})()}
							</button>
						);
					})}
				</div>

				<div className="p-6 pt-2 flex gap-3">
					<Button variant="outline" className="flex-1 rounded-full" onClick={onClose}>
						Cancelar
					</Button>
					<Button
						className="flex-1 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
						onClick={handleConfirm}
						disabled={saving}
					>
						{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
						Confirmar
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default OtherPlansSheet;
